import { UPLOADED_IMAGE_MAX_BYTES } from '../appConfig';

/** The picked file is not an image the browser can decode (e.g. HEIC outside Safari). */
export class UnsupportedImageError extends Error {
  constructor() {
    super('Formato de imagen no soportado por este navegador o app. Usa JPG, PNG o WEBP para asegurar la compatibilidad');
    this.name = 'UnsupportedImageError';
  }
}

interface NormalizeImageOptions {
  maxWidth: number;
  maxHeight: number;
  /** Starting JPEG quality, 0–100 (as in Capacitor's camera options). */
  quality: number;
  maxBytes?: number;
}

// Lowest JPEG quality (0–100) tried before shrinking the image further
const MIN_QUALITY = 50;
const QUALITY_STEP = 10;
const SCALE_STEP = 0.8;
const MAX_ATTEMPTS = 12;

const base64ToBlob = (base64: string): Blob => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes]);
};

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

const encodeJpeg = (bitmap: ImageBitmap, width: number, height: number, quality: number): Promise<Blob | null> => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) return Promise.resolve(null);
  // JPEG has no transparency: paint transparent areas (e.g. of a PNG) white, not black
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, width, height);
  context.drawImage(bitmap, 0, 0, width, height);
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
};

/**
 * Re-encodes a picked image (base64, any format the browser can decode) as a JPEG
 * that fits within maxWidth × maxHeight, keeping its aspect ratio, and weighs at
 * most `maxBytes`: the quality is lowered first, then the size. Returns the JPEG
 * as base64.
 *
 * Needed on the web, where Capacitor's picker returns the original file untouched
 * (its size and quality options only apply on Android and iOS). Throws
 * UnsupportedImageError when the browser cannot decode the file.
 */
export const normalizeImage = async (
  base64: string,
  { maxWidth, maxHeight, quality, maxBytes = UPLOADED_IMAGE_MAX_BYTES }: NormalizeImageOptions
): Promise<string> => {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(base64ToBlob(base64), { imageOrientation: 'from-image' });
  } catch {
    throw new UnsupportedImageError();
  }

  try {
    let scale = Math.min(1, maxWidth / bitmap.width, maxHeight / bitmap.height);
    // Whole percentages, so repeated steps land exactly on MIN_QUALITY
    let jpegQuality = quality;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      const width = Math.max(1, Math.round(bitmap.width * scale));
      const height = Math.max(1, Math.round(bitmap.height * scale));
      const jpeg = await encodeJpeg(bitmap, width, height, jpegQuality / 100);
      if (!jpeg) throw new Error('Could not encode the image');
      if (jpeg.size <= maxBytes) return await blobToBase64(jpeg);

      if (jpegQuality - QUALITY_STEP >= MIN_QUALITY) jpegQuality -= QUALITY_STEP;
      else scale *= SCALE_STEP;
    }
    throw new Error('Could not compress the image under the size limit');
  } finally {
    bitmap.close();
  }
};
